import React, { useState, useEffect } from 'react';
import { AlertCircle, TrendingUp, LogOut } from 'lucide-react';
import AlertList from '../components/AlertList.tsx';
import NPSSurvey from '../components/NPSSurvey.tsx';
import { VoiceCharting } from '../components/VoiceCharting.tsx';
import { BillingCodes } from '../components/BillingCodes.tsx';
import { PatientPortal } from '../components/PatientPortal.tsx';
import { useAuth } from '../components/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';

interface Alert {
  id: string;
  lab_name: string;
  lab_value: number;
  reference_range: string;
  patient_name: string;
  mrn: string;
  classification: 'Actionable' | 'FYI';
  reasoning: string;
  trend: 'stable' | 'up' | 'down';
  model_used: 'claude' | 'mistral';
  confidence: number;
  timestamp: string;
}

type TabType = 'phase1' | 'phase2' | 'phase3';

interface PhaseTab {
  id: TabType;
  label: string;
  description: string;
  icon: string;
}

const AlertDashboard: React.FC = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNPS, setShowNPS] = useState(false);
  const [activePhase, setActivePhase] = useState<TabType>('phase1');
  const [stats, setStats] = useState({
    total: 0,
    actionable: 0,
    fyi: 0,
    reduction_percent: 68
  });

  const phases: PhaseTab[] = [
    { id: 'phase1', label: 'Phase 1: Decision Filter', description: 'Lab result triage', icon: '🧠' },
    { id: 'phase2', label: 'Phase 2: Voice + Billing', description: 'Documentation automation', icon: '🎙️' },
    { id: 'phase3', label: 'Phase 3: Patient Portal', description: 'Patient engagement', icon: '👥' }
  ];

  useEffect(() => {
    loadMockAlerts();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const loadMockAlerts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/alerts`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();

      if (!data.success || !data.alerts) {
        throw new Error('Failed to fetch alerts');
      }

      const apiAlerts: Alert[] = data.alerts.map((a: any) => ({
        id: a.id,
        lab_name: a.lab_name,
        lab_value: a.lab_value,
        reference_range: a.reference_range || 'N/A',
        patient_name: `Patient ${a.patient_id}`,
        mrn: a.patient_id,
        classification: a.classification,
        reasoning: a.reasoning,
        trend: a.trend || 'stable',
        model_used: a.model_used || 'mock',
        confidence: a.confidence || 0.85,
        timestamp: new Date(a.created_at).toISOString()
      }));

      setAlerts(apiAlerts);

      const actionableCount = apiAlerts.filter(a => a.classification === 'Actionable').length;
      const fyiCount = apiAlerts.filter(a => a.classification === 'FYI').length;
      const total = apiAlerts.length;
      const reductionPercent = total > 0 ? Math.round(((total - actionableCount) / total) * 100) : 0;

      setStats({
        total,
        actionable: actionableCount,
        fyi: fyiCount,
        reduction_percent: reductionPercent
      });

      setLoading(false);

      setTimeout(() => setShowNPS(true), 30000);
    } catch (error) {
      console.error('Error loading alerts:', error);
      setStats({
        total: 0,
        actionable: 0,
        fyi: 0,
        reduction_percent: 0
      });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading 60-day demo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <TrendingUp size={32} />
              <h1 className="text-4xl font-bold">EHR-AI 60-Day Demo</h1>
            </div>
            <div className="flex items-center gap-3">
              {user?.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border-2 border-white/40" />}
              <span className="text-blue-100 text-sm">{user?.name}</span>
              <button onClick={handleLogout} className="flex items-center gap-1 text-blue-200 hover:text-white text-sm transition-colors">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </div>
          <p className="text-blue-100 text-lg">Complete end-to-end workflow: Phase 1 → Phase 2 → Phase 3</p>
          <p className="text-blue-200 text-sm mt-2">✅ Decision filter (-68% alerts) • ✅ Voice charting (-75% time) • ✅ Patient portal (same-day notes)</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-8">
        {/* Phase Tabs */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {phases.map((phase) => (
              <button
                key={phase.id}
                onClick={() => setActivePhase(phase.id)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  activePhase === phase.id
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <p className="text-3xl mb-2">{phase.icon}</p>
                <p className="font-semibold text-gray-900">{phase.label}</p>
                <p className="text-sm text-gray-600">{phase.description}</p>
              </button>
            ))}
          </div>

          {/* Phase Content */}
          <div className="space-y-8">
            {activePhase === 'phase1' && (
              <>
                {/* Stats for Phase 1 */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-600 mb-2">Total Results</div>
                    <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
                    <div className="text-sm font-medium text-gray-600 mb-2">Actionable</div>
                    <div className="text-3xl font-bold text-red-600">{stats.actionable}</div>
                    <div className="text-xs text-gray-500 mt-2">Require review</div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
                    <div className="text-sm font-medium text-gray-600 mb-2">FYI Only</div>
                    <div className="text-3xl font-bold text-blue-600">{stats.fyi}</div>
                    <div className="text-xs text-gray-500 mt-2">Informational</div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
                    <div className="text-sm font-medium text-gray-600 mb-2">Alert Reduction</div>
                    <div className="text-3xl font-bold text-green-600">{stats.reduction_percent}%</div>
                    <div className="text-xs text-gray-500 mt-2">vs baseline</div>
                  </div>
                </div>

                {/* Alert List */}
                <AlertList alerts={alerts} />

                {/* Footer Notes */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Decision Filter Demo (15+ Patients)</h3>
                      <p className="text-sm text-blue-700">
                        The AI Service analyzes each lab result against patient baseline, clinical conditions, and recent trends.
                        <strong> Actionable </strong> alerts (red) require immediate review.
                        <strong> FYI </strong> alerts (blue) are informational only.
                      </p>
                      <p className="text-xs text-blue-600 mt-2">
                        Mock data includes: CKD variants, Diabetes, AFib, HTN, Pediatric, Dialysis, Acute Kidney Injury, and more.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activePhase === 'phase2' && (
              <div className="space-y-8">
                <VoiceCharting />
                <BillingCodes />
              </div>
            )}

            {activePhase === 'phase3' && <PatientPortal />}
          </div>
        </div>

        {/* NPS Survey */}
        {showNPS && activePhase === 'phase1' && (
          <div className="mt-8">
            <NPSSurvey onClose={() => setShowNPS(false)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertDashboard;
